import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert } from "react-bootstrap";
import { CgEye, CgTrash } from "react-icons/cg";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Toaster from "../Commaon/Toaster";
import Spinnerr from "../Commaon/Spinner";
import { formatDate } from "../../utils/date";
import DeleteConfirmModal from "../Commaon/DeleteConfirmModal";

const formatCurrency = (value) =>
  value || value === 0 ? `₹ ${value}` : "-";

const shortId = (value) => (value ? String(value).slice(-6) : "-");

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [deleteOrderId, setDeleteOrderId] = useState(null);

  const loadUserDetails = async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/user/${id}`);
      const data = response.data;

      if (data.s === 200 && data.r?._id) {
        setUser(data.r);
      } else {
        setUser(null);
        setSnack({ message: data.m || "Failed to fetch user details", type: "error" });
      }
    } catch (err) {
      console.error("Failed to fetch user details:", err);
      setSnack({ message: "An error occurred", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const deleteOrder = async (orderId) => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/user/${id}/orders/${orderId}`, {
        method: "DELETE",
      });

      if (response?.data?.s === 200) {
        setUser((prev) => {
          if (!prev) return prev;
          const orders = (prev.orders || []).filter((order) => order._id !== orderId);
          return { ...prev, orders, totalOrders: orders.length };
        });
        setSnack({ message: "Order deleted successfully", type: "success" });
      } else {
        setSnack({
          message: response?.data?.m || "Failed to delete order",
          type: "error",
        });
      }
    } catch (err) {
      console.error("Failed to delete order:", err);
      setSnack({ message: "Failed to delete order", type: "error" });
    } finally {
      setLoading(false);
      setDeleteOrderId(null);
    }
  };

  const orders = user?.orders || [];

  return (
    <div fluid className="py-5 pl-6 ml-2">
      <Spinnerr show={loading} />
      {snack && <Toaster notification={snack} onClose={() => setSnack(null)} />}

      {user && (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h4 className="mb-1">User Details</h4>
              <div className="text-muted">Profile, account status, and order history</div>
            </div>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/users")}>
              Back
            </button>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-12 col-lg-4">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-3 mb-4">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                      style={{ width: 64, height: 64, background: "#0d6efd", fontSize: 28 }}
                    >
                      {user.fullName ? user.fullName.charAt(0).toUpperCase() : "U"}
                    </div>
                    <div>
                      <h5 className="mb-1">{user.fullName || "No Name Provided"}</h5>
                      <span className={`badge ${user.isActive ? "bg-success" : "bg-secondary"}`}>
                        {user.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>

                  <div className="row g-3">
                    <Detail label="Email" value={user.email || "Not provided"} />
                    <Detail label="Mobile" value={user.mobileNumber || "Not provided"} />
                    <Detail label="Role" value={user.role || "-"} />
                    <Detail label="Joined On" value={formatDate(user.createdAt)} />
                    <Detail label="Last Updated" value={formatDate(user.updatedAt)} />
                    <Detail label="Last Login" value={formatDate(user.lastLogin)} />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-8">
              <div className="row g-3">
                <Summary title="Total Orders" value={user.totalOrders || 0} />
                <Summary
                  title="Completed Orders"
                  value={orders.filter((order) => order.orderStatus === "Completed").length}
                />
                <Summary
                  title="Pending Orders"
                  value={orders.filter((order) => order.orderStatus === "Pending").length}
                />
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Orders</h5>
                <span className="badge bg-light text-dark">{orders.length} total</span>
              </div>

              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Sr.</th>
                      <th>Order ID</th>
                      <th>Order Number</th>
                      <th>Customer</th>
                      <th>Payment</th>
                      <th>Order Status</th>
                      <th>Lab Status</th>
                      <th>Hard Copy</th>
                      <th>Final Amount</th>
                      <th>Order Date</th>
                      <th style={{ width: 110 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length > 0 ? (
                      orders.map((order, index) => (
                        <tr key={order._id}>
                          <td>{index + 1}</td>
                          <td>{shortId(order._id)}</td>
                          <td>{order.orderNumber || "-"}</td>
                          <td>{order.members?.[0]?.fullName || user.fullName || "-"}</td>
                          <td>{order.paymentStatus || "-"}</td>
                          <td>
                            <StatusBadge value={order.orderStatus} />
                          </td>
                          <td>
                            <StatusBadge value={order.deliveryStatus} />
                          </td>
                          <td>{order.hardCopy ? "Yes" : "No"}</td>
                          <td>{formatCurrency(order.finalAmount)}</td>
                          <td>{formatDate(order.createdAt)}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => navigate(`/user/${id}/orders/${order._id}`)}
                              >
                                <CgEye size={16} />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => setDeleteOrderId(order._id)}
                              >
                                <CgTrash size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="11" className="text-center text-muted py-4">
                          No orders found for this user
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {!user && !loading && (
        <Alert variant="info" className="shadow-sm border-0">
          No user details available.
        </Alert>
      )}

      <DeleteConfirmModal
        show={Boolean(deleteOrderId)}
        count={1}
        singular="order"
        plural="orders"
        loading={loading}
        onConfirm={() => deleteOrder(deleteOrderId)}
        onCancel={() => setDeleteOrderId(null)}
      />
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div className="col-12">
    <div className="text-muted small">{label}</div>
    <div className="fw-semibold">{value}</div>
  </div>
);

const Summary = ({ title, value }) => (
  <div className="col-12 col-md-4">
    <div className="card shadow-sm border-0 h-100">
      <div className="card-body">
        <div className="text-muted small">{title}</div>
        <div className="fs-3 fw-bold">{value}</div>
      </div>
    </div>
  </div>
);

const StatusBadge = ({ value }) => {
  const status = value || "-";
  const className =
    status === "Completed"
      ? "bg-success"
      : status === "Pending"
      ? "bg-warning text-dark"
      : status === "Cancelled"
      ? "bg-danger"
      : "bg-secondary";

  return <span className={`badge ${className}`}>{status}</span>;
};

export default UserDetails;
